import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FolderKanban, Plus } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { DataTable, type Column } from '@/components/shared/data-table';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/misc';
import { usePagination } from '@/hooks/use-pagination';
import { useDebounce } from '@/hooks/use-debounce';
import { formatDate, formatMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useProjects } from './api';
import { ProjectDialog } from './project-dialog';
import {
  BILLING_MODEL_LABELS,
  HEALTH_DOT,
  HEALTH_LABELS,
  PROJECT_STATUS_LABELS,
  PROJECT_STATUS_VARIANT,
} from './labels';
import type { Project } from './types';

export function ProjectsPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const pagination = usePagination();
  const [search, setSearch] = useState('');
  // Seeded from the link that brought you here, e.g. the dashboard's
  // "upcoming projects" card, so the list shows what the number counted.
  const [status, setStatus] = useState(params.get('status') ?? '');
  const [mine, setMine] = useState(false);
  const [creating, setCreating] = useState(false);
  const debounced = useDebounce(search, 300);

  const { data, isLoading, error, refetch } = useProjects({
    page: pagination.page,
    limit: pagination.limit,
    q: debounced || undefined,
    status: status || undefined,
    mine: mine || undefined,
  });

  const columns: Column<Project>[] = [
    {
      key: 'name',
      header: 'Project',
      cell: (row) => (
        <div className="flex items-start gap-2.5">
          <span
            className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', HEALTH_DOT[row.health])}
            title={HEALTH_LABELS[row.health]}
          />
          <div className="min-w-0">
            <div className="truncate font-medium">{row.name}</div>
            <div className="truncate text-xs text-muted-foreground">
              <span className="font-mono">{row.code}</span> · {row.company.name}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'model',
      header: 'Billing',
      className: 'text-sm text-muted-foreground',
      cell: (row) => BILLING_MODEL_LABELS[row.billingModel],
    },
    {
      key: 'value',
      header: 'Contract',
      className: 'tabular text-sm font-medium',
      cell: (row) => formatMoney(row.contractValue, row.currency),
    },
    {
      key: 'budget',
      header: 'Budget',
      cell: (row) =>
        row.budgetHours > 0 ? (
          <span className="tabular text-sm text-muted-foreground">{row.budgetHours}h</span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      key: 'items',
      header: 'Work',
      className: 'tabular text-sm text-muted-foreground',
      cell: (row) => `${row.workItemCount} items`,
    },
    {
      key: 'end',
      header: 'Target end',
      className: 'whitespace-nowrap text-sm',
      cell: (row) => (row.targetEndDate ? formatDate(row.targetEndDate) : '—'),
    },
    {
      key: 'manager',
      header: 'Manager',
      className: 'text-sm text-muted-foreground',
      cell: (row) => row.manager?.name ?? 'Unassigned',
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <Badge variant={PROJECT_STATUS_VARIANT[row.status]}>
          {PROJECT_STATUS_LABELS[row.status]}
        </Badge>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Projects"
        description="What is being delivered, what it is worth, and how much of the budget is left."
        actions={
          <Can permission="project.project.create">
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              New project
            </Button>
          </Can>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search name, code or client…"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            pagination.reset();
          }}
          className="h-9 max-w-xs"
        />
        <Select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            pagination.reset();
          }}
          className="h-9 w-auto min-w-[9rem]"
        >
          <option value="">All statuses</option>
          {Object.entries(PROJECT_STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Button
          variant={mine ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => {
            setMine((value) => !value);
            pagination.reset();
          }}
        >
          On my projects
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error ? 'Could not load projects.' : null}
        onRetry={() => void refetch()}
        meta={data?.meta}
        onPageChange={pagination.setPage}
        onRowClick={(row) => navigate(`/delivery/projects/${row.id}`)}
        emptyTitle="No projects yet"
        emptyDescription="A project is where delivery, time and milestone billing all hang together."
        emptyAction={
          <Can permission="project.project.create">
            <Button onClick={() => setCreating(true)}>
              <FolderKanban className="h-4 w-4" />
              New project
            </Button>
          </Can>
        }
      />

      {creating && (
        <ProjectDialog
          onClose={() => setCreating(false)}
          onSaved={(project) => navigate(`/delivery/projects/${project.id}`)}
        />
      )}
    </div>
  );
}
